import { CabinetHero } from "../_shared/cabinet-hero";
import { MiniMap } from "../_shared/mini-map";
import { DirectionsLink } from "../_shared/map-link";
import { PageShell, Stack } from "../_shared/page-shell";
import { Avatar } from "../_shared/avatar";
import { Card } from "../_shared/card";
import {
  IconBolt,
  IconClock,
  IconHeart,
  IconMail,
  IconPhone,
  IconPin,
  IconTooth,
} from "../_shared/icons";
import { teamMembers } from "../_data/mock";
import {
  CABINET_ADDRESS,
  CABINET_CITY,
  CABINET_POSTAL_CODE,
} from "../_data/constants";
import { usePractice } from "../_shared/practice.provider";

const services = [
  {
    id: "ortho-enfant",
    icon: IconTooth,
    label: "Orthodontie pédiatrique",
    detail: "Traitements précoces dès 6 ans",
  },
  {
    id: "ortho-adulte",
    icon: IconHeart,
    label: "Orthodontie adulte",
    detail: "Aligneurs invisibles et bagues céramiques",
  },
  {
    id: "urgence",
    icon: IconBolt,
    label: "Urgences orthodontiques",
    detail: "Réponse sous 4 heures (jours ouvrés)",
  },
];

const hours = [
  { day: "Lundi", value: "9h00 – 19h00" },
  { day: "Mardi", value: "9h00 – 19h00" },
  { day: "Mercredi", value: "9h00 – 19h00" },
  { day: "Jeudi", value: "9h00 – 19h00" },
  { day: "Vendredi", value: "9h00 – 19h00" },
  { day: "Samedi", value: "9h00 – 13h00" },
  { day: "Dimanche", value: "Fermé", off: true },
];

const today = new Date().getDay();
// JS: Sunday=0, Monday=1 ... Saturday=6 — mapping to hours index (Monday=0)
const todayIndex = today === 0 ? 6 : today - 1;

export function CabinetPage() {
  const { practice } = usePractice();

  return (
    <>
      <CabinetHero title="Cabinet" />
      <PageShell>
        <Stack>
          <Card
            title={
              <span className="flex flex-col gap-0.5">
                <span className="text-[11.5px] font-bold uppercase tracking-[0.12em] text-[var(--sf-primary)]">
                  {practice.city}
                </span>
                <span className="text-[22px] font-extrabold tracking-tight">
                  Cabinet {practice.name}
                </span>
              </span>
            }
            multilineTitle
          >
            <p className="text-[14.5px] leading-snug text-[var(--text-2)]">
              Spécialisé en orthodontie pédiatrique et adulte, notre cabinet
              accompagne enfants et adultes avec les techniques les plus
              récentes — aligneurs invisibles, bagues céramiques et appareils
              multi-attaches.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-2.5">
              <ContactTile
                icon={<IconPhone width={16} height={16} />}
                label="Appeler"
                value="01 42 00 00 00"
                href="tel:+33142000000"
              />
              <ContactTile
                icon={<IconMail width={16} height={16} />}
                label="Écrire"
                value="contact@cabinet.fr"
                href="mailto:contact@cabinet.fr"
              />
            </div>
          </Card>

          <Card
            title="Adresse"
            icon={<IconPin width={20} height={20} />}
            onClick={() => {}}
          >
            <div className="flex flex-col gap-2.5">
              <p className="text-[13.5px] font-semibold text-[var(--text-1)]">
                {CABINET_ADDRESS}
              </p>
              <p className="text-[12.5px] text-[var(--text-2)]">
                {CABINET_POSTAL_CODE} {CABINET_CITY}
              </p>
              <MiniMap />
              <DirectionsLink className="mt-3 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[var(--sf-primary)] px-5 text-[15px] font-semibold text-white shadow-sm transition active:scale-[0.99] sm:mx-auto sm:max-w-sm">
                <IconPin width={16} height={16} strokeWidth={2.4} />
                Lancer l'itinéraire
              </DirectionsLink>
            </div>
          </Card>

          <Card title="Horaires" icon={<IconClock width={20} height={20} />}>
            <ul className="flex flex-col gap-1.5 text-[13.5px]">
              {hours.map((h, i) => {
                const isToday = i === todayIndex;
                return (
                  <li
                    key={h.day}
                    className={`flex items-center justify-between gap-3 rounded-xl px-2 py-1 ${isToday ? "bg-[var(--sf-primary-100)]" : ""}`}
                  >
                    <span
                      className={`${isToday ? "font-bold text-[var(--sf-primary)]" : "text-[var(--text-2)]"}`}
                    >
                      {h.day}
                      {isToday ? (
                        <span className="ml-1.5 text-[10.5px] font-semibold uppercase tracking-wide">
                          aujourd'hui
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={`font-semibold ${
                        h.off
                          ? "text-[var(--text-3)]"
                          : isToday
                            ? "text-[var(--sf-primary)]"
                            : "text-[var(--text-1)]"
                      }`}
                    >
                      {h.value}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card title="L'équipe">
            <ul className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
              {teamMembers.map((m) => (
                <li
                  key={m.id}
                  className="flex w-24 shrink-0 flex-col items-center gap-2 rounded-2xl bg-[var(--surface-2)] p-2.5 text-center"
                >
                  <Avatar
                    initials={m.initials}
                    photoUrl={m.photoUrl}
                    alt={m.fullName}
                    size="xl"
                  />
                  <div className="min-w-0">
                    <p className="truncate text-[12px] font-bold text-[var(--text-1)]">
                      {m.fullName}
                    </p>
                    <p className="truncate text-[10.5px] text-[var(--text-2)]">
                      {m.role}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Spécialités">
            <ul className="flex flex-col gap-2">
              {services.map((s) => {
                const Icon = s.icon;
                return (
                  <li
                    key={s.id}
                    className="flex items-center gap-3 rounded-2xl bg-[var(--surface-2)] p-3"
                  >
                    <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--sf-primary-100)] text-[var(--sf-primary)]">
                      <Icon width={20} height={20} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13.5px] font-semibold text-[var(--text-1)]">
                        {s.label}
                      </p>
                      <p className="text-[12px] text-[var(--text-2)]">
                        {s.detail}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        </Stack>
      </PageShell>
    </>
  );
}

function ContactTile({
  icon,
  label,
  value,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href: string;
}) {
  return (
    <a
      href={href}
      className="flex flex-col gap-1 rounded-2xl bg-[var(--surface-2)] p-3 transition active:scale-[0.98]"
    >
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--text-3)]">
        <span className="inline-flex size-5 items-center justify-center rounded-full bg-[var(--surface-1)] text-[var(--sf-primary)]">
          {icon}
        </span>
        {label}
      </span>
      <span className="truncate text-[13.5px] font-semibold text-[var(--text-1)]">
        {value}
      </span>
    </a>
  );
}
